import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  ExchangeOptionsDetails,
  ExchangeOptionsGeneral,
} from '../../sj-orm/models/assets/categories';
import {
  BasicAssetType,
  ExchangeOptionsType,
  OptionStyleType,
  StrategyBasicPositionType,
} from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type ExchangeOptions = ExchangeOptionsFormGeneralType &
  ExchangeOptionsFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const ExchangeOptionsList = [
  {
    label: 'forms_assets:exchangeOptions.enums.optionType.call',
    key: ExchangeOptionsType.call,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.optionType.put',
    key: ExchangeOptionsType.put,
  },
];

const OptionStyleList = [
  {
    label: 'forms_assets:exchangeOptions.enums.styleType.european',
    key: OptionStyleType.european,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.styleType.american',
    key: OptionStyleType.american,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.styleType.asian',
    key: OptionStyleType.asian,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.styleType.quasiAmerican',
    key: OptionStyleType.quasiAmerican,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.styleType.other',
    key: OptionStyleType.other,
  },
];

export const BasicAssetList = [
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.stocks',
    key: BasicAssetType.stocks,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.commodity',
    key: BasicAssetType.commodity,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.currency',
    key: BasicAssetType.currency,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.cryptocurrency',
    key: BasicAssetType.cryptocurrency,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.bonds',
    key: BasicAssetType.bonds,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.index',
    key: BasicAssetType.index,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.interestRate',
    key: BasicAssetType.interestRate,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.futuresContract',
    key: BasicAssetType.futuresContract,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.basicAssetType.other',
    key: BasicAssetType.other,
  },
];

const StrategyBasicPositionList = [
  {
    label: 'forms_assets:exchangeOptions.enums.strategy.buyingCall',
    key: StrategyBasicPositionType.buyingCall,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.strategy.sellingCall',
    key: StrategyBasicPositionType.sellingCall,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.strategy.buyingPut',
    key: StrategyBasicPositionType.buyingPut,
  },
  {
    label: 'forms_assets:exchangeOptions.enums.strategy.sellingPut',
    key: StrategyBasicPositionType.sellingPut,
  },
];

type ExchangeOptionsHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.TEXT;
  type: QuestionType.SELECT_FROM_LIST;
  amount: QuestionType.NUMBER;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR;
  basicAssetName: QuestionType.TEXT;
  basicAssetType: QuestionType.SELECT_FROM_LIST;
  otherBasicAssetType: QuestionType.TEXT;
  basicAssetAmount: QuestionType.NUMBER;
  broker: QuestionType.PICK_ARRAY_OF_DTO;
};

type ExchangeOptionsHelpDetails = {
  isin: QuestionType.TEXT;
  ticker: QuestionType.TEXT;
  exchangeName: QuestionType.TEXT;
  optionStyle?: QuestionType.SELECT_FROM_LIST;
  otherOptionStyle?: QuestionType.TEXT;
  basicAssetType?: QuestionType.SELECT_FROM_LIST;
  otherBasicAssetType?: QuestionType.TEXT;
  basicAssetAmount?: QuestionType.NUMBER;
  optionPeriod?: QuestionType.DATE_TIME;
  sellerName?: QuestionType.TEXT;
  sellerDetails?: QuestionType.TEXT_MULTILINE;
  brokerAccountNumber?: QuestionType.TEXT;
  dealReserves?: QuestionType.CHECKBOX;
  dealReservesData?: QuestionType.TEXT;
  strategyBasicPosition?: QuestionType.SELECT_FROM_LIST;
  strategyDescription?: QuestionType.TEXT_MULTILINE;
  attachments?: QuestionType.FILE_UPLOAD;
};

type ExchangeOptionsFormGeneralType = TDtoWrapper<
  ExchangeOptionsGeneral,
  ExchangeOptionsHelpGeneral
>;

type ExchangeOptionsFormDetailsType = TDtoWrapper<
  ExchangeOptionsDetails,
  ExchangeOptionsHelpDetails
>;

const ExchangeOptionsFormGeneral: ExchangeOptionsFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:exchangeOptions.inputs.type.label',
    tooltip: 'forms_assets:exchangeOptions.inputs.type.tooltip',
    required: true,
    value: ExchangeOptionsType.put,
    items: ExchangeOptionsList,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:exchangeOptions.inputs.amount.label',
    required: true,
    value: 0,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:exchangeOptions.inputs.purchaseValue.label',
    tooltip: 'forms_assets:exchangeOptions.inputs.purchaseValue.tooltip',
    equivalentValueTooltip:
      // eslint-disable-next-line sonarjs/no-duplicate-string
      'forms_assets:common.inputs.equivalentValue.tooltip',
    required: true,
    value: 0,
  },
  basicAssetType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:exchangeOptions.inputs.basicAssetType.label',
    tooltip: 'forms_assets:exchangeOptions.inputs.basicAssetType.tooltip',
    items: BasicAssetList,
    // required: true,
    value: BasicAssetType.commodity,
  },
  otherBasicAssetType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.otherBasicAssetType.label',
    required: true,
    value: '',
    showIf: {
      basicAssetType: {
        value: BasicAssetType.other,
      },
    },
  },
  basicAssetName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.basicAssetName.label',
    // required: true,
    value: '',
  },
  basicAssetAmount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:exchangeOptions.inputs.basicAssetAmount.label',
    // required: true,
    value: 0,
  },
  broker: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:exchangeOptions.inputs.broker.label',
    colName: CollectionName.CONTACTS,
    // required: true,
    value: [],
    filter: (contact) =>
      [ContactType.BROKER, ContactType.BANK].includes((contact as unknown as ContactDto).type),
    dtoType: [ContactType.BANK, ContactType.BROKER],
  },
};

const ExchangeOptionsFormDetails: ExchangeOptionsFormDetailsType = {
  isin: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.isin.label',
    order: 3,
  },
  ticker: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.ticker.label',
    order: 4,
  },
  exchangeName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.exchangeName.label',
    value: '',
    order: 5,
  },
  optionStyle: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:exchangeOptions.inputs.optionStyle.label',
    items: OptionStyleList,
    order: 6,
  },
  otherOptionStyle: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.otherOptionStyle.label',
    showIf: {
      optionStyle: {
        value: OptionStyleType.other,
      },
    },
    order: 7,
  },
  optionPeriod: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:exchangeOptions.inputs.optionPeriod.label',
    tooltip: 'forms_assets:exchangeOptions.inputs.optionPeriod.tooltip',
    order: 8,
  },
  sellerName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.sellerName.label',
    order: 9,
  },
  sellerDetails: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:exchangeOptions.inputs.sellerDetails.label',
    order: 10,
  },
  brokerAccountNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.brokerAccountNumber.label',
    order: 11,
  },
  dealReserves: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:exchangeOptions.inputs.dealReserves.label',
    tooltip: 'forms_assets:exchangeOptions.inputs.dealReserves.tooltip',
    order: 13,
  },
  dealReservesData: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.dealReservesData.label',
    showIf: {
      dealReserves: {
        value: true,
      },
    },
    order: 14,
  },
  strategyBasicPosition: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:exchangeOptions.inputs.strategyBasicPosition.label',
    tooltip: 'forms_assets:exchangeOptions.inputs.strategyBasicPosition.tooltip',
    items: StrategyBasicPositionList,
    order: 15,
  },
  strategyDescription: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:exchangeOptions.inputs.strategyDescription.label',
    order: 16,
  },
  attachments: baseAssetFields.attachments,
};

const ExchangeOptionsForm = [
  ExchangeOptionsFormGeneral,
  ExchangeOptionsFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder(['buyingPrice', 'strikePrice']),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { ExchangeOptionsForm };
